import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, Space, Typography } from 'antd';
import moment, { Moment } from 'moment';
import DatePicker from '@components/DatePicker';

const { Text } = Typography;

type useNetWorkBlockType = {
  renderParamTitle: (title: string) => JSX.Element;
  setBlockingType: Dispatch<SetStateAction<number>>;
  blockingType: number;
  blockStartDate: Moment | null;
  setBlockStartDate: Dispatch<SetStateAction<Moment | null>>;
  blockEndDate: Moment | null;
  setBlockEndDate: Dispatch<SetStateAction<Moment | null>>;
};

const useNetWorkBlock = ({
  renderParamTitle,
  setBlockingType,
  blockingType,
  blockStartDate,
  setBlockStartDate,
  blockEndDate,
  setBlockEndDate,
}: useNetWorkBlockType) => {
  const { t: translate } = useTranslation(['device-actions-modal']);
  const netWorkBlockStep = useMemo(() => {
    return (
      <div className="network-block-content">
        <Space
          direction="vertical"
          size="large"
          align="center"
          style={{ width: '100%' }}
        >
          <div>
            <div>{renderParamTitle('block-type')}</div>
            <div className="network-block-radio">
              <Radio.Group
                defaultValue={1}
                value={blockingType}
                onChange={e => {
                  setBlockingType(e.target.value);
                }}
              >
                <Radio.Button value={1}>
                  {translate('steps.network-block.blocking-type-permanent')}
                </Radio.Button>
                <Radio.Button value={2}>
                  {translate('steps.network-block.blocking-type-renew')}
                </Radio.Button>
                <Radio.Button value={3}>
                  {translate('steps.network-block.blocking-type-temporary')}
                </Radio.Button>
              </Radio.Group>
            </div>
          </div>

          <div className="network-block-blocking-explanation">
            <Text type="warning">
              {blockingType === 1 &&
                translate('steps.network-block.blocking-type-permanent-exp')}
              {blockingType === 2 &&
                translate('steps.network-block.blocking-type-renew-exp')}
              {blockingType === 3 &&
                translate('steps.network-block.blocking-type-temporary-exp')}
            </Text>
          </div>

          {/* SPECIFIC PARAMETERS */}

          <div style={{ display: 'flex' }}>
            <div style={{ flex: '1', paddingRight: '10px' }}>
              {renderParamTitle('initial-validity')}
              <div className="network-block-datepicker">
                <DatePicker
                  value={blockStartDate}
                  onChange={(date: Moment | null) => {
                    setBlockStartDate(date);
                  }}
                  disabledDate={current => {
                    return (
                      (current &&
                        current.startOf('day') < moment().startOf('day')) ||
                      (blockingType === 3 &&
                        blockEndDate !== null &&
                        current.startOf('day') > blockEndDate.startOf('day'))
                    );
                  }}
                  hideFooter
                />
              </div>
            </div>
            {blockingType === 3 && (
              <div style={{ flex: '1', paddingLeft: '10px' }}>
                {renderParamTitle('end-validity')}
                <div className="network-block-datepicker">
                  <DatePicker
                    value={blockEndDate}
                    onChange={(date: Moment | null) => {
                      setBlockEndDate(date);
                    }}
                    disabledDate={current => {
                      return (
                        current &&
                        (current.startOf('day') < moment().startOf('day') ||
                          (blockStartDate !== null &&
                            current.startOf('day') <
                              blockStartDate.startOf('day')))
                      );
                    }}
                    hideFooter
                  />
                </div>
              </div>
            )}
          </div>
        </Space>
      </div>
    );
  }, [
    blockEndDate,
    blockStartDate,
    blockingType,
    renderParamTitle,
    setBlockEndDate,
    setBlockStartDate,
    setBlockingType,
    translate,
  ]);
  return {
    netWorkBlockStep,
  };
};

export default useNetWorkBlock;
